import api, { getErrorMessage } from '@/api/axios';
import type { ApiResponse, UserInfo, SessionInfo } from '@/types';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  userRole: 'operator' | 'comissionist';
  phone?: string;
  termsAccepted: boolean;
  privacyAccepted: boolean;
}

export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
  userRole: 'operator' | 'comissionist';
  phone?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ValidateResetTokenResponse {
  valid: boolean;
  email: string | null;
}

class AuthService {
  private readonly basePath = '/admin/auth';

  async login(username: string, password: string): Promise<ApiResponse<UserInfo>> {
    const response = await api.post<ApiResponse<UserInfo>>(
      `${this.basePath}/login`,
      { username, password }
    );
    return response.data;
  }

  async logout(): Promise<void> {
    await api.post(`${this.basePath}/logout`);
  }

  async getProfile(): Promise<ApiResponse<UserInfo>> {
    const response = await api.get<ApiResponse<UserInfo>>(`${this.basePath}/profile`);
    return response.data;
  }

  async checkSession(): Promise<ApiResponse<SessionInfo>> {
    const response = await api.get<ApiResponse<SessionInfo>>(`${this.basePath}/check`);
    return response.data;
  }

  async refreshSession(): Promise<void> {
    await api.post(`${this.basePath}/refresh`);
  }

  async register(data: RegisterRequest): Promise<ApiResponse<null>> {
    const response = await api.post<ApiResponse<null>>(`${this.basePath}/register`, data);
    return response.data;
  }

  async updateProfile(data: UpdateProfileRequest): Promise<ApiResponse<null>> {
    const response = await api.put<ApiResponse<null>>(`${this.basePath}/profile`, data);
    return response.data;
  }

  async changePassword(data: ChangePasswordRequest): Promise<ApiResponse<null>> {
    const response = await api.post<ApiResponse<null>>(
      `${this.basePath}/change-password`,
      data
    );
    return response.data;
  }

  async forgotPassword(email: string): Promise<ApiResponse<null>> {
    const response = await api.post<ApiResponse<null>>(
      `${this.basePath}/forgot-password?email=${encodeURIComponent(email)}`
    );
    return response.data;
  }

  async validateResetToken(token: string): Promise<ApiResponse<ValidateResetTokenResponse>> {
    const response = await api.get<ApiResponse<ValidateResetTokenResponse>>(
      `${this.basePath}/validate-reset-token?token=${encodeURIComponent(token)}`
    );
    return response.data;
  }

  async resetPassword(data: ResetPasswordRequest): Promise<ApiResponse<null>> {
    const response = await api.post<ApiResponse<null>>(
      `${this.basePath}/reset-password`,
      data
    );
    return response.data;
  }
}

export const authService = new AuthService();
export default authService;